import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAppVersionTelemetry } from './useAppVersionTelemetry';
import { ROUTES_TRELLO_MAP, getRouteTrelloMeta, type RouteTrelloMeta } from './routesTrelloMap';
import { 
  downloadMarkdownReport, 
  downloadHtmlReport, 
  printAuditReport 
} from './exportAuditReport';
import { 
  ShieldCheck, 
  FileText, 
  Code, 
  Printer, 
  ExternalLink, 
  CheckCircle2, 
  AlertTriangle,
  ArrowLeft,
  Search,
  Copy,
  Layers,
  Monitor,
  Smartphone,
  Image as ImageIcon,
  MessageSquare,
  Activity,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { TesterSessionsPanel } from './session-recorder';

export const AuditReportPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialRoute = searchParams.get('route') || '/master/dashboard';
  const [selectedRoute, setSelectedRoute] = useState<string>(initialRoute);
  const [filterText, setFilterText] = useState<string>('');
  const [zoomImageUrl, setZoomImageUrl] = useState<string | null>(null);
  const [activeReportTab, setActiveReportTab] = useState<'routes' | 'sessions'>('routes');

  const telemetry = useAppVersionTelemetry();
  const { localManifest, remoteManifest, isStale, viewport } = telemetry;

  const currentMeta: RouteTrelloMeta = getRouteTrelloMeta(selectedRoute);

  const filteredRoutes = ROUTES_TRELLO_MAP.filter((r) =>
    r.routeName.toLowerCase().includes(filterText.toLowerCase()) ||
    r.routePattern.toLowerCase().includes(filterText.toLowerCase()) ||
    r.trelloCard.toLowerCase().includes(filterText.toLowerCase())
  );

  const handleCopyLink = () => {
    const url = `${window.location.origin}/audit-report?route=${encodeURIComponent(selectedRoute)}`;
    void navigator.clipboard.writeText(url);
    toast.success('Link do relatório copiado para a área de transferência!');
  };

  const isMobile = viewport.width < 768;

  return (
    <>
      <div className="min-h-screen bg-slate-950 text-slate-100 font-sans p-4 md:p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Cabeçalho */}
          <header className="flex flex-col md:flex-row md:items-center md:justify-between pb-6 border-b border-slate-800 gap-4">
            <div className="flex items-center gap-3">
              <Link
                to="/master/dashboard"
                className="p-2 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                title="Voltar ao Sistema"
              >
                <ArrowLeft size={18} />
              </Link>
              <div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="text-blue-500" size={24} />
                  <h1 className="text-xl md:text-2xl font-black tracking-tight text-white">
                    Portal Público de Auditoria & Homologação
                  </h1>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Plataforma Aluno Conectado • Rastreabilidade Trello & Assinatura de Versão
                </p>
              </div>
            </div>

            {/* Ações Rápidas de Exportação */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => downloadMarkdownReport(currentMeta, localManifest, remoteManifest, viewport)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg bg-slate-900 border border-slate-700 text-slate-200 hover:bg-slate-800 transition-colors"
              >
                <FileText size={14} className="text-emerald-400" />
                Baixar .MD
              </button>
              <button
                onClick={() => downloadHtmlReport(currentMeta, localManifest, remoteManifest, viewport)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg bg-slate-900 border border-slate-700 text-slate-200 hover:bg-slate-800 transition-colors"
              >
                <Code size={14} className="text-blue-400" />
                Baixar .HTML
              </button>
              <button
                onClick={() => printAuditReport(currentMeta, localManifest, remoteManifest, viewport)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg bg-blue-600 hover:bg-blue-500 text-white transition-colors"
              >
                <Printer size={14} />
                Exportar / PDF
              </button>
              <button
                onClick={handleCopyLink}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg bg-purple-600/20 border border-purple-500/40 text-purple-300 hover:bg-purple-600/30 transition-colors"
                title="Copiar link público desta rota"
              >
                <Copy size={14} />
                Copiar Link
              </button>
            </div>
          </header>

          {/* Banner de Integridade de Cache */}
          {isStale ? (
            <div className="p-4 bg-red-950/60 border border-red-800/80 rounded-2xl flex items-start gap-3 text-red-200">
              <AlertTriangle className="text-red-400 shrink-0 mt-0.5" size={20} />
              <div className="text-xs leading-relaxed">
                <p className="font-bold text-sm text-red-300">Atenção: Cache Desatualizado Detectado no Navegador</p>
                <p className="mt-1">
                  A versão local ativa no cliente é <strong>{localManifest.version}</strong> ({localManifest.gitCommit}), mas o servidor de produção/CDN já possui a versão <strong>{remoteManifest?.version || 'Nova'}</strong>.
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-emerald-950/40 border border-emerald-800/60 rounded-2xl flex items-center justify-between gap-3 text-emerald-200">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="text-emerald-400 shrink-0" size={20} />
                <div className="text-xs">
                  <p className="font-bold text-emerald-300">Ambiente 100% Sincronizado com Produção / CDN</p>
                  <p className="text-emerald-400/80 mt-0.5">
                    Versão ativa: <strong>{localManifest.version}</strong> ({localManifest.gitCommit}) • Build: {localManifest.buildTimestamp}
                  </p>
                </div>
              </div>
              <span className="hidden md:inline-flex text-[0.70rem] font-mono bg-emerald-900/60 text-emerald-300 px-2.5 py-1 rounded-full border border-emerald-700/50">
                HASH: {localManifest.gitCommit}
              </span>
            </div>
          )}

          {/* Grid de Metadados Globais */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 space-y-2">
              <div className="flex items-center justify-between text-xs text-slate-400 font-bold uppercase tracking-wider">
                <span>Metadados do Build</span>
                <Layers size={14} className="text-blue-400" />
              </div>
              <div className="space-y-1.5 text-xs font-mono">
                <div className="flex justify-between">
                  <span className="text-slate-400">Versão:</span>
                  <span className="text-blue-400 font-bold">{localManifest.version}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Commit Git:</span>
                  <span className="text-slate-200">{localManifest.gitCommit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Branch:</span>
                  <span className="text-slate-200">{localManifest.gitBranch}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Ambiente:</span>
                  <span className="text-emerald-400 font-bold uppercase">{localManifest.environment}</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 space-y-2">
              <div className="flex items-center justify-between text-xs text-slate-400 font-bold uppercase tracking-wider">
                <span>Ambiente do Testador</span>
                {isMobile ? (
                  <Smartphone size={14} className="text-purple-400" />
                ) : (
                  <Monitor size={14} className="text-purple-400" />
                )}
              </div>
              <div className="space-y-1.5 text-xs font-mono">
                <div className="flex justify-between">
                  <span className="text-slate-400">Viewport:</span>
                  <span className="text-purple-300 font-bold">{viewport.width} x {viewport.height} px</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Modo:</span>
                  <span className="text-slate-200">{isMobile ? 'Mobile (< 768px)' : 'Desktop'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Cache Status:</span>
                  <span className={isStale ? 'text-red-400 font-bold' : 'text-emerald-400 font-bold'}>
                    {isStale ? '⚠️ Desatualizado' : '🟢 Sincronizado'}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 space-y-2">
              <div className="flex items-center justify-between text-xs text-slate-400 font-bold uppercase tracking-wider">
                <span>Cards & Discussões Trello</span>
                <MessageSquare size={14} className="text-amber-400" />
              </div>
              <div className="space-y-1.5 text-xs">
                <p className="font-bold text-white truncate">{currentMeta.trelloCard}</p>
                <p className="text-slate-400 text-[0.70rem]">
                  {currentMeta.relatedTrelloCards?.length || 0} cards vinculados • {currentMeta.relatedTrelloCards?.reduce((a, c) => a + c.commentsCount, 0) || 0} comentários
                </p>
                <span className="inline-block text-[0.65rem] bg-purple-900/60 text-purple-200 font-bold px-2 py-0.5 rounded border border-purple-700/50">
                  {currentMeta.status}
                </span>
              </div>
            </div>
          </div>

          {/* NAVEGAÇÃO DE ABAS NO PORTAL: ROTAS VS SESSÕES */}
          <div className="flex items-center justify-between bg-slate-900/80 p-1.5 rounded-2xl border border-slate-800">
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setActiveReportTab('routes')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  activeReportTab === 'routes'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
                }`}
              >
                <Layers size={14} />
                Rastreabilidade Trello por Rota
              </button>
              <button
                onClick={() => setActiveReportTab('sessions')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  activeReportTab === 'sessions'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
                }`}
              >
                <Activity size={14} />
                Auditoria de Sessões & Replay
                <span className="bg-emerald-500 text-white text-[0.60rem] px-2 py-0.5 rounded-full font-extrabold uppercase">
                  Novo
                </span>
              </button>
            </div>

            <span className="text-xs text-slate-500 hidden md:inline px-3 font-medium">
              {activeReportTab === 'routes' ? 'Catálogo Completo de Rotas e Cards' : 'Inspeção Detalhada de Testadores, Coordenadas e Gravação'}
            </span>
          </div>

          {/* CONTEÚDO CONDICIONAL */}
          {activeReportTab === 'sessions' ? (
            <div className="rounded-2xl overflow-hidden border border-slate-800 h-[700px] shadow-2xl">
              <TesterSessionsPanel currentVersion={localManifest.version} />
            </div>
          ) : (
            <>
          {/* Seletor e Detalhe da Rota */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Coluna Esquerda: Catálogo de Rotas */}
            <div className="md:col-span-4 space-y-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-3 text-slate-500" />
                <input
                  type="text"
                  placeholder="Filtrar rotas ou cards..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div className="space-y-1.5 max-h-[600px] overflow-y-auto pr-1">
                {filteredRoutes.map((r) => {
                  const isSelected = selectedRoute === r.routePattern;
                  return (
                    <button
                      key={r.routePattern}
                      onClick={() => setSelectedRoute(r.routePattern)}
                      className={`w-full text-left p-3 rounded-xl border transition-all text-xs ${
                        isSelected
                          ? 'bg-blue-600/10 border-blue-500/50 text-white'
                          : 'bg-slate-900/60 border-slate-800 text-slate-300 hover:bg-slate-800/80 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold truncate">{r.routeName}</span>
                        <span className="text-[0.65rem] px-1.5 py-0.5 rounded bg-slate-800 font-mono text-slate-400 shrink-0">
                          {r.status}
                        </span>
                      </div>
                      <p className="font-mono text-[0.68rem] text-blue-400 mt-1 truncate">{r.routePattern}</p>
                      <p className="text-[0.68rem] text-slate-400 truncate mt-0.5">📋 {r.trelloCard}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Coluna Direita: Ficha Completa da Rota Selecionada */}
            <div className="md:col-span-8 bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between pb-4 border-b border-slate-800 gap-2">
                <div>
                  <span className="text-[0.70rem] uppercase tracking-wider font-bold text-slate-400 block">
                    Especificação Técnica da Tela
                  </span>
                  <h2 className="text-lg font-black text-white">{currentMeta.routeName}</h2>
                  <code className="text-xs font-mono text-blue-400">{currentMeta.routePattern}</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-purple-900/60 text-purple-200 border border-purple-700/50">
                    {currentMeta.status}
                  </span>
                  {currentMeta.trelloUrl && (
                    <a
                      href={currentMeta.trelloUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-bold text-purple-400 hover:text-purple-300 hover:underline p-1.5"
                    >
                      Trello <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              </div>

              {/* Decisão Arquitetural */}
              <div className="bg-purple-950/20 border border-purple-800/40 rounded-xl p-4 text-xs leading-relaxed text-purple-200">
                <strong className="text-purple-300 block mb-1">Decisão Arquitetural & Comportamento Esperado:</strong>
                {currentMeta.lastDecision}
              </div>

              {/* Recursos Validados */}
              <div className="space-y-2">
                <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                  Recursos & Componentes Homologados:
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {currentMeta.testedFeatures.map((feat, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2.5 rounded-lg bg-slate-950 border border-slate-800 text-xs text-slate-200">
                      <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Histórico & Discussões do Trello com Fotos e Anexos */}
              {currentMeta.relatedTrelloCards && currentMeta.relatedTrelloCards.length > 0 && (
                <div className="space-y-4 pt-4 border-t border-slate-800">
                  <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <MessageSquare size={14} className="text-purple-400" />
                      Histórico & Discussões no Trello ({currentMeta.relatedTrelloCards.length} cards):
                    </span>
                    <span className="text-[0.68rem] font-mono text-purple-400">
                      {currentMeta.relatedTrelloCards.reduce((acc, c) => acc + c.commentsCount, 0)} comentários
                    </span>
                  </h3>

                  <div className="space-y-4">
                    {currentMeta.relatedTrelloCards.map((card) => (
                      <div key={card.id} className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div>
                            <span className="text-xs font-bold text-white block">📋 {card.name}</span>
                            <span className="text-[0.68rem] text-slate-400">
                              Última atividade: {new Date(card.dateLastActivity).toLocaleString('pt-BR')}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[0.65rem] font-mono px-2 py-0.5 rounded bg-purple-950 text-purple-300 border border-purple-800">
                              {card.listName}
                            </span>
                            <a
                              href={card.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[0.68rem] text-blue-400 hover:text-blue-300 hover:underline inline-flex items-center gap-1"
                            >
                              Card <ExternalLink size={10} />
                            </a>
                          </div>
                        </div>

                        {card.labels.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {card.labels.map((l, lIdx) => (
                              <span key={lIdx} className="text-[0.62rem] px-2 py-0.5 rounded bg-slate-900 border border-slate-700 text-slate-300">
                                {l.name}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Galeria de Anexos do Card */}
                        {((card.attachments && card.attachments.length > 0) || (card.descImages && card.descImages.length > 0)) && (
                          <div className="pt-2 border-t border-slate-800/80">
                            <span className="text-[0.68rem] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 mb-2">
                              <ImageIcon size={12} className="text-purple-400" />
                              Capturas de Tela & Anexos do Card:
                            </span>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                              {card.attachments?.filter(a => a.isImage).map((att) => (
                                <div
                                  key={att.id}
                                  className="aspect-video rounded-lg overflow-hidden border border-slate-800 bg-slate-900 cursor-pointer hover:border-purple-500 transition-colors"
                                  onClick={() => setZoomImageUrl(att.previewUrl || att.url)}
                                  title="Clique para ampliar"
                                >
                                  <img src={att.thumbnailUrl || att.previewUrl || att.url} alt={att.name} className="w-full h-full object-cover" loading="lazy" />
                                </div>
                              ))}
                              {card.descImages?.map((img, idx) => (
                                <div
                                  key={`desc_img_${idx}`}
                                  className="aspect-video rounded-lg overflow-hidden border border-slate-800 bg-slate-900 cursor-pointer hover:border-purple-500 transition-colors"
                                  onClick={() => setZoomImageUrl(img.url)}
                                  title="Clique para ampliar"
                                >
                                  <img src={img.url} alt={img.alt} className="w-full h-full object-cover" loading="lazy" />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Timeline de comentários */}
                        {card.comments.length > 0 ? (
                          <div className="space-y-2 pt-2 border-t border-slate-800/80">
                            <span className="text-[0.68rem] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                              Discussão Cronológica ({card.comments.length} mensagens):
                            </span>
                            {card.comments.map((comm) => (
                              <div key={comm.id} className="p-3 bg-slate-900 border border-slate-800/80 rounded-lg text-xs space-y-1.5">
                                <div className="flex items-center justify-between text-[0.72rem]">
                                  <div className="flex items-center gap-1.5 font-bold text-slate-200">
                                    <div className="w-4 h-4 rounded-full bg-purple-600 text-[0.55rem] text-white flex items-center justify-center font-black">
                                      {comm.initials}
                                    </div>
                                    <span>{comm.author}</span>
                                  </div>
                                  <span className="text-slate-500 font-mono text-[0.68rem]">{new Date(comm.date).toLocaleString('pt-BR')}</span>
                                </div>
                                <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">{comm.text}</p>
                                {comm.images && comm.images.length > 0 && (
                                  <div className="flex flex-wrap gap-2 pt-1">
                                    {comm.images.map((img, iIdx) => (
                                      <div
                                        key={iIdx}
                                        className="w-20 h-14 rounded overflow-hidden border border-slate-700 bg-black cursor-pointer hover:border-blue-400"
                                        onClick={() => setZoomImageUrl(img.url)}
                                      >
                                        <img src={img.url} alt={img.alt} className="w-full h-full object-cover" loading="lazy" />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-500 italic">Nenhum comentário registrado.</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          </>
          )}
        </div>
      </div>

      {/* Lightbox para Zoom em Imagens/Prints */}
      {zoomImageUrl && (
        <div
          className="fixed inset-0 z-[100000] bg-black/85 backdrop-blur-xs flex items-center justify-center p-4"
          onClick={() => setZoomImageUrl(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh] bg-slate-900 p-2 rounded-2xl border border-slate-700 shadow-2xl">
            <button
              onClick={() => setZoomImageUrl(null)}
              className="absolute -top-3 -right-3 p-1.5 bg-red-600 hover:bg-red-700 text-white rounded-full shadow-lg"
              title="Fechar Visualização"
            >
              <X size={16} />
            </button>
            <img
              src={zoomImageUrl}
              alt="Visualização em Alta Resolução"
              className="max-h-[82vh] max-w-full rounded-xl object-contain"
            />
            <div className="mt-2 text-center">
              <a
                href={zoomImageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-400 hover:underline inline-flex items-center gap-1 font-mono"
                onClick={(e) => e.stopPropagation()}
              >
                Abrir imagem original em nova aba <ExternalLink size={11} />
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
